import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { S, A, TONES, DARK } from './tokens';

export type MenuItem = {
  key: string;
  label: string;
  /** Material icon name. */
  icon?: string;
  /** Second line in smaller type: what the action does. */
  hint?: string;
  onSelect?: () => void;
  /** A Genesis route. */
  to?: string;
  /** Any other address. */
  href?: string;
  newTab?: boolean;
  /** Destructive: drawn in red. Put these in the last group. */
  danger?: boolean;
  disabled?: boolean;
  /** Why it's disabled, or more detail, on hover. */
  title?: string;
  /** One choice of several (like the theme): shown with a check when it's the current one. */
  checked?: boolean;
};

type MenuProps = {
  anchor: HTMLElement;
  /** Groups are separated by a line. */
  groups: MenuItem[][];
  label: string;
  /** Closed: `returnFocus` puts focus back on the button that opened it. */
  onClose: (returnFocus: boolean) => void;
  align?: 'start' | 'end';
  /** Opens above the anchor, e.g. for a menu at the bottom of the screen. */
  above?: boolean;
  minWidth?: number;
  /** Shown above the items (e.g. who is signed in). */
  header?: React.ReactNode;
  testId?: string;
};

const GAP = 6;
const EDGE = 8;

const useStyles = createUseStyles({
  menu: {
    position: 'fixed',
    zIndex: 2000,
    boxSizing: 'border-box',
    maxWidth: 'calc(100vw - 16px)',
    padding: 6,
    background: A.surface,
    border: `1px solid ${S.line}`,
    borderRadius: 10,
    boxShadow: A.pop,
    fontFamily: S.ui,
    color: S.fg,
    outline: 'none',
    transformOrigin: 'top right',
    animation: '$gxMenuIn 120ms ease-out'
  },
  '@keyframes gxMenuIn': {
    from: { opacity: 0, transform: 'translateY(-4px) scale(0.98)' },
    to: { opacity: 1, transform: 'none' }
  },
  header: { padding: '8px 10px 10px', borderBottom: `1px solid ${S.line}`, margin: '-2px -6px 6px' },
  sep: { height: 1, background: S.line, margin: '6px -6px' },
  item: {
    all: 'unset',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    width: '100%',
    minHeight: 34,
    padding: '8px 10px',
    borderRadius: 6,
    fontSize: 14,
    lineHeight: '18px',
    cursor: 'pointer',
    color: S.fg,
    textDecoration: 'none',
    '& .material-icons': { fontSize: 18, color: S.muted, flexShrink: 0 },
    '&:hover, &:focus': { background: S.chrome },
    '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${S.fg}` },
    '&[aria-disabled="true"]': { color: '#A5A8AE', cursor: 'not-allowed', background: 'none', '& .material-icons': { color: '#BFC2C7' } },
    [DARK]: { '&[aria-disabled="true"]': { color: TONES.gray.dot, '& .material-icons': { color: S.fieldLine } } }
  },
  danger: {
    color: S.red,
    '& .material-icons': { color: S.red },
    '&:hover, &:focus': { background: 'rgba(200, 55, 55, 0.07)' },
    [DARK]: { '&:hover, &:focus': { background: TONES.red.bg } }
  },
  text: { display: 'grid', gap: 2, minWidth: 0, flex: 1 },
  check: { marginLeft: 'auto', alignSelf: 'center', '&.material-icons': { color: S.fg } },
  hint: { fontSize: 12, lineHeight: '16px', color: S.muted }
});

const focusables = (root: HTMLElement | null) =>
  root ? Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"]), [role="menuitemradio"]:not([aria-disabled="true"])')) : [];

/**
 * A menu of actions in a popover next to the button that opened it. Arrow
 * keys move between items, Escape closes it and returns to the button, a
 * click outside closes it, and it follows its button when things scroll.
 */
export const Menu: React.FC<MenuProps> = ({ anchor, groups, label, onClose, align = 'end', above, minWidth = 220, header, testId }) => {
  const c = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Next to the button, flipped above or kept inside the window when there's no room.
  const place = React.useCallback(() => {
    const menu = ref.current;
    if (!menu) return;
    const a = anchor.getBoundingClientRect();
    const m = menu.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = align === 'end' ? a.right - m.width : a.left;
    left = Math.max(EDGE, Math.min(left, vw - m.width - EDGE));
    const below = a.bottom + GAP;
    const over = a.top - GAP - m.height;
    let top = above ? over : below;
    if (!above && below + m.height > vh - EDGE && over >= EDGE) top = over;
    if (above && over < EDGE) top = below;
    top = Math.max(EDGE, Math.min(top, vh - m.height - EDGE));
    setPos(p => (p && p.top === top && p.left === left ? p : { top, left }));
  }, [anchor, align, above]);

  useLayoutEffect(place, [place]);

  // Focus the first item once placed (for keyboard users; a mouse click doesn't show the ring).
  useEffect(() => {
    if (!pos) return;
    const first = focusables(ref.current)[0];
    if (first) first.focus({ preventScroll: true });
    else if (ref.current) ref.current.focus({ preventScroll: true });
  }, [!!pos]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor.contains(t)) return;
      onCloseRef.current(false);
    };
    // When something scrolls (the page, or a panel scrolling itself), the menu follows its
    // button; it closes only once the button is gone or out of view.
    let frame = 0;
    const onScroll = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const r = anchor.getBoundingClientRect();
        const gone = !anchor.isConnected || r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth;
        if (gone) onCloseRef.current(false);
        else place();
      });
    };
    const onResize = () => onCloseRef.current(false);
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [anchor, place]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const items = focusables(ref.current);
    const i = items.indexOf(document.activeElement as HTMLElement);
    const move = (n: number) => { e.preventDefault(); items[(n + items.length) % items.length]?.focus(); };
    if (e.key === 'ArrowDown') move(i + 1);
    else if (e.key === 'ArrowUp') move(i < 0 ? items.length - 1 : i - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(items.length - 1);
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(true); }
    // Back on the menu button, so Tab carries on from there.
    else if (e.key === 'Tab') onClose(true);
  };

  const select = (item: MenuItem) => (e: React.MouseEvent) => {
    if (item.disabled) { e.preventDefault(); return; }
    // Focus goes back to the menu button first, so a dialog opened by the item returns there when it closes.
    onClose(!item.to && !item.href);
    if (item.onSelect) item.onSelect();
  };

  const visible = groups.map(g => g.filter(Boolean)).filter(g => g.length > 0);

  const renderItem = (item: MenuItem) => {
    const cls = [c.item, item.danger ? c.danger : ''].join(' ');
    const body = (
      <>
        {item.icon && <i className="material-icons" aria-hidden>{item.icon}</i>}
        <span className={c.text}>
          <span>{item.label}</span>
          {item.hint && <span className={c.hint}>{item.hint}</span>}
        </span>
        {item.checked && <i className={`material-icons ${c.check}`} aria-hidden>check</i>}
      </>
    );
    const common = {
      role: item.checked === undefined ? 'menuitem' : 'menuitemradio',
      'aria-checked': item.checked === undefined ? undefined : item.checked,
      className: cls,
      title: item.title,
      'aria-disabled': item.disabled || undefined,
      'data-menu-item': item.key,
      tabIndex: -1,
      onClick: select(item)
    };
    if (item.to && !item.disabled) return <Link key={item.key} {...common} to={item.to} target={item.newTab ? '_blank' : undefined} rel={item.newTab ? 'noopener noreferrer' : undefined}>{body}</Link>;
    if (item.href && !item.disabled) {
      return <a key={item.key} {...common} href={item.href} target={item.newTab ? '_blank' : undefined} rel="noopener noreferrer">{body}</a>;
    }
    return <button key={item.key} {...common} type="button">{body}</button>;
  };

  return ReactDOM.createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      tabIndex={-1}
      className={c.menu}
      style={{ minWidth, top: pos ? pos.top : -9999, left: pos ? pos.left : -9999, visibility: pos ? 'visible' : 'hidden' }}
      onKeyDown={onKeyDown}
      // The menu is rendered at the end of the page, but React passes its clicks up to the row it belongs to.
      onClick={e => e.stopPropagation()}
      data-testid={testId}
    >
      {header && <div className={c.header}>{header}</div>}
      {visible.map((group, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && <div className={c.sep} role="separator" />}
          {group.map(renderItem)}
        </React.Fragment>
      ))}
    </div>,
    document.body
  );
};

const useTriggerStyles = createUseStyles({
  trigger: {
    all: 'unset',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    color: S.muted,
    cursor: 'pointer',
    '& .material-icons': { fontSize: 20 },
    '&:hover, &[aria-expanded="true"]': { background: S.chromeHover, color: S.fg },
    '&:focus-visible': A.focus
  }
});

type RowMenuProps = {
  /** Spoken name of the button, e.g. "Actions for Home page". */
  label: string;
  groups: MenuItem[][];
  icon?: string;
  testId?: string;
  className?: string;
};

/** The ⋯ button at the end of a row and its menu. */
export const RowMenu: React.FC<RowMenuProps> = ({ label, groups, icon = 'more_horiz', testId, className }) => {
  const c = useTriggerStyles();
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  if (!groups.some(g => g.length > 0)) return null;
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={[c.trigger, className].filter(Boolean).join(' ')}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
        data-row-menu
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); } }}
      >
        <i className="material-icons" aria-hidden>{icon}</i>
      </button>
      {open && btn.current && (
        <Menu
          anchor={btn.current}
          groups={groups}
          label={label}
          testId={testId ? `${testId}-menu` : undefined}
          onClose={(returnFocus) => { setOpen(false); if (returnFocus) btn.current?.focus(); }}
        />
      )}
    </>
  );
};
